/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import { EuiButton, EuiContextMenuItem, EuiContextMenuPanel, EuiPopover } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { RulesetType } from '@modules/network-security-lib/types'

type Props = {
  onClick: (rulesetType: RulesetType) => void
}

const CreateButton: React.FunctionComponent<Props> = ({ onClick }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const intl = useIntl()

  const onButtonClick = () => {
    setIsPopoverOpen(!isPopoverOpen)
  }

  const closePopover = () => {
    setIsPopoverOpen(false)
  }

  const handleSelection = (rulesetType: RulesetType) => {
    onClick(rulesetType)
    closePopover()
  }

  const items = [
    <EuiContextMenuItem
      key='ipFilter'
      icon='logstashFilter'
      onClick={() => handleSelection('ipFilter')}
    >
      <FormattedMessage id='networkSecurity.createIpFilter' defaultMessage='IP filter' />
    </EuiContextMenuItem>,
    <EuiContextMenuItem
      key='privateConnection'
      icon='lock'
      onClick={() => handleSelection('privateConnection')}
    >
      <FormattedMessage
        id='networkSecurity.createPrivateConnection'
        defaultMessage='Private connection'
      />
    </EuiContextMenuItem>,
  ]

  const button = (
    <EuiButton
      iconType='arrowDown'
      iconSide='right'
      onClick={onButtonClick}
      aria-label={intl.formatMessage({
        id: 'networkSecurity.createButton',
        defaultMessage: 'Create',
      })}
      color='primary'
      fill={true}
    >
      <FormattedMessage id='networkSecurity.createButton' defaultMessage='Create' />
    </EuiButton>
  )

  return (
    <EuiPopover
      id='createTrafficFilterPopover'
      button={button}
      isOpen={isPopoverOpen}
      closePopover={closePopover}
      panelPaddingSize='none'
      anchorPosition='downRight'
    >
      <EuiContextMenuPanel items={items} />
    </EuiPopover>
  )
}

export default CreateButton
